import PropTypes from 'prop-types';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { HEATMAP_COLORS, HEATMAP_THRESHOLDS } from '../../../utils/constants/heatmapColors';

/**
 * HeatmapLegend - Color coding legend for heatmap visualization
 *
 * Displays 5 color squares with labels explaining the color coding.
 * Labels are dynamically responsive to the selected Color Coding Option
 * per TD-1962 requirements.
 *
 * Threshold config per metric — currently all use the same values
 * (see heatmapColors.js), but structured for extensibility.
 */

const COLOR_CODING = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING];

const DEFAULT_THRESHOLDS = { low: HEATMAP_THRESHOLDS.LOW, high: HEATMAP_THRESHOLDS.HIGH };

const THRESHOLD_CONFIG = {
  [COLOR_CODING['EBIT Margin (%)']]: DEFAULT_THRESHOLDS,
  [COLOR_CODING['CAGR 3Y (%)']]: DEFAULT_THRESHOLDS,
  [COLOR_CODING['CAGR 4Y (%)']]: DEFAULT_THRESHOLDS
};

const getLegendItems = (colorCoding) => {
  const thresholds = THRESHOLD_CONFIG[colorCoding] || DEFAULT_THRESHOLDS;

  return [
    { id: 'bottom-quartile', color: HEATMAP_COLORS.WORST, label: 'Bottom Quartile' },
    {
      id: 'below-low',
      color: HEATMAP_COLORS.WORST_REST,
      label: `< ${thresholds.low}%`,
      ariaLabel: `less than ${thresholds.low}%`
    },
    {
      id: 'neutral',
      color: HEATMAP_COLORS.NEUTRAL,
      label: `${thresholds.low}% – ${thresholds.high}%`,
      ariaLabel: `${thresholds.low}% to ${thresholds.high}%`
    },
    {
      id: 'above-high',
      color: HEATMAP_COLORS.BEST_REST,
      label: `> ${thresholds.high}%`,
      ariaLabel: `greater than ${thresholds.high}%`
    },
    { id: 'top-quartile', color: HEATMAP_COLORS.BEST, label: 'Top Quartile' }
  ];
};

const HeatmapLegend = ({ colorCoding } = {}) => {
  const items = getLegendItems(colorCoding);

  return (
    <div className="heatmap-legend" role="group" aria-label="Heatmap color legend">
      {items.map((item) => (
        <div key={item.id} className="heatmap-legend-item">
          <span
            className="heatmap-legend-color"
            style={{ backgroundColor: item.color }}
            role="img"
            aria-label={item.ariaLabel || item.label}
          />
          <span className="heatmap-legend-label" aria-hidden="true">
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
};

HeatmapLegend.propTypes = {
  colorCoding: PropTypes.string
};

export default HeatmapLegend;
